import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Button,
  Typography,
  LinearProgress
} from '@material-ui/core';
import HistoryIcon from '@material-ui/icons/History';
import { useDispatch, useSelector } from 'react-redux';
import { createCellNote, saveCellNote, deleteCellNote } from 'actions';
import { Alert, ConfirmDelete } from 'components';
import NoteAttribution from './NoteAttribution';
import NoteEditor from './NoteEditor';
import NoteHistory from './NoteHistory';

const useStyles = makeStyles(theme => ({
  root: {},
  actions: {
    '& > * + *': {
      marginLeft: theme.spacing(1)
    }
  },
  /* Pushed away from the action cluster so it reads as a view, not another action */
  historyButton: {
    marginLeft: 'auto!important',
    color: theme.palette.text.secondary
  },
  content: {
    marginTop: theme.spacing(1),
    maxHeight: 160,
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  }
}));

const SAVE_FAILED_MESSAGE = 'Unable to save the note. Please try again.';
const DELETE_FAILED_MESSAGE = 'Unable to delete the note. Please try again.';
const LOAD_FAILED_MESSAGE = 'Unable to load notes for this device.';
const STALE_NOTE_MESSAGE =
  'This note belongs to a different device. Close this and reopen it.';

/**
 * Pulls a message out of an unsuccessful response, which carries either a top level message or a
 * map of per field validation messages.
 */
const retrieveErrorMessage = (response, fallback) => {
  const data = response && response.value && response.value.data;

  if (data && data.message) {
    return data.message;
  }

  const validationMessages = (data && data.validationMessages) || {};
  const field = Object.keys(validationMessages)[0];
  const messages = field ? validationMessages[field] : null;

  return messages && messages.length ? messages[0] : fallback;
};

const CellNotes = props => {
  const {
    className,
    cell,
    cellNotes,
    loading,
    failed,
    editableRight,
    deletableRight,
    title,
    ...rest
  } = props;

  const classes = useStyles();
  const dispatch = useDispatch();

  const { sessionUser } = useSelector(state => state.session);

  const editable =
    !editableRight || sessionUser.rights.indexOf(editableRight) > -1;
  const deletable =
    !deletableRight || sessionUser.rights.indexOf(deletableRight) > -1;

  const [editorOpen, setEditorOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editedNote, setEditedNote] = useState(null);
  const [deletedNote, setDeletedNote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  /* Guards the state updates below, which land after the write resolves and so can outlive the
     component if the page is left mid save */
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const latestNote = cellNotes.length > 0 ? cellNotes[0] : null;

  const handleAdd = () => {
    setEditedNote(null);
    setSaveError(null);
    setEditorOpen(true);
  };

  const handleEdit = note => {
    setEditedNote(note);
    setSaveError(null);
    setEditorOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditedNote(null);
    setSaveError(null);
  };

  const handleDeleteRequest = note => {
    setDeletedNote(note);
    setDeleteError(null);
  };

  const handleDeleteCancel = () => {
    setDeletedNote(null);
    setDeleteError(null);
  };

  /* The editor is left open until the write is known to have succeeded, so a rejected save does
     not discard what was typed */
  const handleSave = content => {
    /* The note being edited is checked against the cell on screen rather than trusted, so that an
       edit cannot reach a note belonging to a different cell should this card ever outlive a
       change of cell. The keyed subtree in the details views should prevent that, but the write is
       the thing worth making unconditionally safe */
    if (editedNote && editedNote.cell !== cell.id) {
      setSaveError(STALE_NOTE_MESSAGE);
      return;
    }

    const action = editedNote
      ? saveCellNote(editedNote.id, { content })
      : createCellNote(cell.id, { content });

    setSaving(true);
    setSaveError(null);

    Promise.resolve(dispatch(action)).then(
      response => {
        if (!mounted.current) {
          return;
        }
        setSaving(false);
        if (response?.value?.data?.success) {
          handleEditorClose();
        } else {
          setSaveError(retrieveErrorMessage(response, SAVE_FAILED_MESSAGE));
        }
      },
      () => {
        if (!mounted.current) {
          return;
        }
        setSaving(false);
        setSaveError(SAVE_FAILED_MESSAGE);
      }
    );
  };

  /* Matches the save path: the dialog stays open and reports the failure rather than closing as
     though the note had been deleted */
  const handleConfirmDelete = () => {
    /* Scoped for the same reason as the save above */
    if (deletedNote.cell !== cell.id) {
      setDeleteError(STALE_NOTE_MESSAGE);
      return;
    }

    setDeleting(true);
    setDeleteError(null);

    Promise.resolve(dispatch(deleteCellNote(deletedNote.id))).then(
      response => {
        if (!mounted.current) {
          return;
        }
        setDeleting(false);
        if (response?.value?.data?.success) {
          setDeletedNote(null);
        } else {
          setDeleteError(retrieveErrorMessage(response, DELETE_FAILED_MESSAGE));
        }
      },
      () => {
        if (!mounted.current) {
          return;
        }
        setDeleting(false);
        setDeleteError(DELETE_FAILED_MESSAGE);
      }
    );
  };

  return (
    <>
      <Card {...rest} className={clsx(classes.root, className)}>
        <CardHeader title={title ? title : 'Notes'} />
        <Divider />
        <CardContent>
          {loading && <LinearProgress />}
          {!loading && failed && (
            <Alert message={LOAD_FAILED_MESSAGE} variant="error" />
          )}
          {!loading && !failed && latestNote && (
            <>
              <NoteAttribution note={latestNote} />
              <Typography className={classes.content} variant="body1">
                {latestNote.content}
              </Typography>
            </>
          )}
          {!loading && !failed && !latestNote && (
            <Typography color="textSecondary" variant="body2">
              No notes recorded
            </Typography>
          )}
        </CardContent>

        <CardActions className={classes.actions}>
          {editable && (
            <Button onClick={handleAdd} size="small" variant="outlined">
              Add
            </Button>
          )}
          {editable && latestNote && (
            <Button
              onClick={() => handleEdit(latestNote)}
              size="small"
              variant="outlined">
              Edit
            </Button>
          )}
          {deletable && latestNote && (
            <Button
              onClick={() => handleDeleteRequest(latestNote)}
              size="small"
              variant="outlined">
              Delete
            </Button>
          )}
          <Button
            className={classes.historyButton}
            disabled={cellNotes.length === 0}
            onClick={() => setHistoryOpen(true)}
            size="small"
            startIcon={<HistoryIcon />}>
            {cellNotes.length > 1 ? `History (${cellNotes.length})` : 'History'}
          </Button>
        </CardActions>
      </Card>

      <NoteEditor
        error={saveError}
        note={editedNote}
        onClose={handleEditorClose}
        onSave={handleSave}
        open={editorOpen}
        saving={saving}
      />

      <NoteHistory
        deletable={deletable}
        editable={editable}
        notes={cellNotes}
        onClose={() => setHistoryOpen(false)}
        onDelete={handleDeleteRequest}
        onEdit={handleEdit}
        open={historyOpen}
      />

      <ConfirmDelete
        deleting={deleting}
        entityType="note"
        message={deleteError}
        onClose={handleDeleteCancel}
        onConfirm={handleConfirmDelete}
        open={deletedNote !== null}
      />
    </>
  );
};

CellNotes.propTypes = {
  cell: PropTypes.object.isRequired,
  cellNotes: PropTypes.array,
  className: PropTypes.string,
  deletableRight: PropTypes.string,
  editableRight: PropTypes.string,
  failed: PropTypes.bool,
  loading: PropTypes.bool,
  title: PropTypes.string
};

CellNotes.defaultProps = {
  cellNotes: [],
  loading: false,
  failed: false
};

export default CellNotes;
